<script setup lang="ts">
/**
 * An image-page's one setting: the picture itself.
 *
 * Nothing else to offer — no caption, no cadrage, no opacity — because the
 * point of this kind is that it draws nothing but the picture.
 */
import { useI18n } from 'vue-i18n'

import type { ImagePageSection } from '@/domain/section'
import ImagePicker from '@/sections/ImagePicker.vue'

const { t } = useI18n()

const props = defineProps<{ section: ImagePageSection }>()
const emit = defineEmits<{ change: [section: ImagePageSection] }>()
</script>

<template>
  <div class="fields">
    <p class="note">{{ t('inspector.imagePage.note') }}</p>

    <ImagePicker
      :source="section.image"
      :label="t('inspector.imagePage.image')"
      :hint="t('inspector.imagePage.imageHint')"
      @change="emit('change', { ...props.section, image: $event })"
    />

    <label class="b-switch">
      <input
        type="checkbox"
        :checked="section.spread"
        @change="
          emit('change', {
            ...props.section,
            spread: ($event.target as HTMLInputElement).checked,
          })
        "
      />
      <span class="b-switch__track" />
      <span class="switch-label">{{ t('inspector.imagePage.spread') }}</span>
    </label>
    <p class="note">{{ t('inspector.imagePage.spreadHint') }}</p>
  </div>
</template>

<style scoped>
.fields {
  display: flex;
  flex-direction: column;
  gap: var(--sp-6);
}

.note {
  margin: 0;
  color: var(--ink-faint);
  font-size: var(--text-xs);
  line-height: 1.5;
}

.switch-label {
  font-size: var(--text-base);
}
</style>
